import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Modal, Stack, Typography } from '@mui/material';
import { FormCheckbox, FormDatepickerField, FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { distributorsContext } from 'pages/distributorManagement/distributorManagementResolver';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Loading from 'shared/components/Loading';
import { SectionHeader } from 'shared/components/massActions';
import {
    DEFAULT_EXPORT_FREIGHT_TEXT,
    DEFAULT_EXPORT_LEAD_TIME_TEXT,
    DEFAULT_EXPORT_PCR_PRICE_CHANGES,
    DEFAULT_MINIMUM_ORDER_QUANTITY_TEXT,
} from 'shared/constants';
import { useGetNationalAccountUsers } from 'shared/hooks';
import { useGetExportDeviations } from 'shared/hooks/useGetExportDeviations';
import { usePdfExport } from 'shared/hooks/usePdfExport';
import { useOnScreenTextContext } from 'shared/providers/provideOnScreenText';
import { TableDataContext } from 'shared/types';
import { formatDatePickerStringWithoutTime, generateProductGroups, generateQuoteNumber } from 'shared/utility';
import {
    CurrentPricesData,
    DistributorsData,
    EndUserDropdown,
    ExportQuoteCurrentPrice,
    NationalAccountUser,
    QuoteEndUser,
    QuotePresentedBy,
} from 'types/backend';
import { AccountNumberParams } from './managePricingResolver';

interface ExportQuoteModalProps {
    open: boolean;
    matchingCustomer: DistributorsData;
    endUserOptions: EndUserDropdown[];
    context: React.Context<TableDataContext<CurrentPricesData>>;
    onClose: () => void;
}

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '10%',
    left: '20%',
    width: '60%',
    bgcolor: 'background.paper',
    border: '2px solid #CCC',
    boxSizing: 'border-box',
    boxShadow: 24,
    p: 0,
    m: 0,
};

export function ExportQuoteModal({ open, matchingCustomer, endUserOptions, context, onClose }: ExportQuoteModalProps) {
    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        clearErrors,
    } = useForm<ExportQuoteCurrentPrice>({
        defaultValues: {
            nationalAccountContact: '',
        },
    });
    const { response, filtersApplied } = useContext(context);
    const [isLoading, setIsLoading] = useState(false);
    const pdfExportMutation = usePdfExport<ExportQuoteCurrentPrice>();
    const exportDeviationsMutation = useGetExportDeviations();
    const { values: customText } = useOnScreenTextContext();

    const params = useParams<AccountNumberParams>();
    const summaryContext = useContext<TableDataContext<DistributorsData>>(distributorsContext);
    const distributorRows: DistributorsData[] = summaryContext.response?.data?.rows || [];
    const matchingDistributor = distributorRows.find((row) => row.customerNumber === params.accountNumber);

    const selectedNationalAccountContact = watch('nationalAccountContact');
    const { data: nationalAccountUsersResponse, isLoading: nationalAccountUsersIsLoading } =
        useGetNationalAccountUsers();
    const nationalAccountDropdownOptions = nationalAccountUsersResponse?.map((aUser) => {
        return {
            value: aUser.name,
            displayText: aUser.name,
        };
    });

    const getNationalAccountContactInfo = useCallback(
        (selectedContact: string): NationalAccountUser | undefined => {
            if (nationalAccountUsersResponse && !nationalAccountUsersIsLoading) {
                return nationalAccountUsersResponse.find((aContact) => {
                    return aContact.name === selectedContact;
                });
            }
        },
        [nationalAccountUsersIsLoading, nationalAccountUsersResponse]
    );

    useEffect(() => {
        if (setValue) {
            setValue('endUserContacts.contact', '');
            setValue('endUserContacts.email', '');
            setValue('endUserContacts.phone', '');
            setValue('presentedBy.name', '');
            setValue('presentedBy.email', '');
            setValue('presentedBy.phone', '');
            setValue('notes.additional', '');
            setValue('increaseValue', undefined);
            setValue('effectiveDate', new Date().toLocaleDateString());
            setValue('isNationalAccount', false);
            setValue('isPriceGroupOverride', false);
        }
        if (selectedNationalAccountContact && setValue) {
            const contactInfo = getNationalAccountContactInfo(selectedNationalAccountContact);
            setValue('isNationalAccount', true);
            if (selectedNationalAccountContact === 'blank') {
                setValue('presentedBy.name', '');
                setValue('presentedBy.email', '');
                setValue('presentedBy.phone', '');
            } else {
                clearErrors('presentedBy.name');
                clearErrors('presentedBy.email');
                clearErrors('presentedBy.phone');

                setValue('presentedBy.name', contactInfo?.name ?? '');
                setValue('presentedBy.email', contactInfo?.emailAddress ?? '');
                setValue('presentedBy.phone', contactInfo?.phone ?? '');
            }
        }
    }, [setValue, response, open, endUserOptions, selectedNationalAccountContact, getNationalAccountContactInfo, clearErrors]);
    const isPriceGroupOverride = watch('isPriceGroupOverride');

    const handlePdfQuoteExport = handleSubmit(async () => {
        setIsLoading(true);
        const exportDeviations = await exportDeviationsMutation.getDeviations(
            matchingCustomer.customerNumber,
            watch('increaseValue'),
            filtersApplied
        );

        const exportEndUsersSet = exportDeviations.reduce((accum, deviation) => {
            accum.add(deviation.endUser);
            return accum;
        }, new Set());

        const exportEndUsers = Array.from(exportEndUsersSet) as string[];

        const isOneEndUser = exportEndUsers.length === 1;

        const selectedEndUser = isOneEndUser
            ? endUserOptions.find((endUser) => endUser.endusername === exportEndUsers[0])
            : undefined;

        if (exportDeviations) {
            const endUserContacts: QuoteEndUser = {
                contact: watch('endUserContacts.contact'),
                email: watch('endUserContacts.email'),
                phone: watch('endUserContacts.phone'),
            };
            const isNationalAccount = watch('isNationalAccount');
            const presentedBy: QuotePresentedBy = {
                name: isNationalAccount ? (watch('nationalAccountContact') === 'blank' ? '' : watch('presentedBy.name')) : matchingDistributor?.territorySalesLeader || '',
                email: isNationalAccount
                    ? watch('presentedBy.email')
                    : matchingDistributor?.territorySalesLeaderEmail || '',
                phone: isNationalAccount
                    ? watch('presentedBy.phone')
                    : matchingDistributor?.territorySalesLeaderPhone || '',
            };

            let endUserGroupName = '';
            let fileName = `export-${new Date().toISOString().split('T')[0]}`;
            if (filtersApplied?.items.some((i) => i.columnField.toLowerCase() === 'pricegroup')) {
                endUserGroupName = exportDeviations[0].priceGroup;
                fileName = `${endUserGroupName}-${new Date().toISOString().split('T')[0]}`;
            }
            if (selectedEndUser) {
                fileName = `${selectedEndUser?.endusername}-${new Date().toISOString().split('T')[0]}`;
            }

            const productGroups = generateProductGroups(exportDeviations);

            if (isPriceGroupOverride) {
                await pdfExportMutation.mutateAsync({
                    data: {
                        ...matchingCustomer,
                        endUserContacts,
                        endUserDetails: undefined,
                        priceGroupOverrideName: selectedEndUser?.enduserpricinggroup,
                        effectiveDate: formatDatePickerStringWithoutTime(watch('effectiveDate')),
                        notes: {
                            additional: watch('notes.additional') ?? '',
                            freight: customText?.pdfExportFreight?.text ?? DEFAULT_EXPORT_FREIGHT_TEXT,
                            leadTime: customText?.pdfExportLeadTime?.text ?? DEFAULT_EXPORT_LEAD_TIME_TEXT,
                            pcrPriceChanges:
                                customText?.exportPcrPriceChanges?.text ?? DEFAULT_EXPORT_PCR_PRICE_CHANGES,
                            minimumOrder: customText?.minimumOrderQuantity?.text ?? DEFAULT_MINIMUM_ORDER_QUANTITY_TEXT,
                        },
                        productGroups: productGroups,
                        presentedBy,
                        quoteDate: formatDatePickerStringWithoutTime(new Date().toLocaleDateString()),
                        quoteNumber: generateQuoteNumber(matchingCustomer.customerNumber, new Date()),
                    },
                    fileName,
                    exportId: 'quote',
                });
            } else {
                await pdfExportMutation.mutateAsync({
                    data: {
                        ...matchingCustomer,
                        endUserContacts,
                        endUserDetails: selectedEndUser,
                        priceGroupOverrideName: undefined,
                        effectiveDate: formatDatePickerStringWithoutTime(watch('effectiveDate')),
                        notes: {
                            additional: watch('notes.additional') ?? '',
                            freight: customText?.pdfExportFreight?.text ?? DEFAULT_EXPORT_FREIGHT_TEXT,
                            leadTime: customText?.pdfExportLeadTime?.text ?? DEFAULT_EXPORT_LEAD_TIME_TEXT,
                            pcrPriceChanges:
                                customText?.exportPcrPriceChanges?.text ?? DEFAULT_EXPORT_PCR_PRICE_CHANGES,
                            minimumOrder: customText?.minimumOrderQuantity?.text ?? DEFAULT_MINIMUM_ORDER_QUANTITY_TEXT,
                        },
                        productGroups: productGroups,
                        presentedBy,
                        quoteDate: formatDatePickerStringWithoutTime(new Date().toLocaleDateString()),
                        quoteNumber: generateQuoteNumber(matchingCustomer.customerNumber, new Date()),
                    },
                    fileName,
                    exportId: 'quote',
                });
            }
            setIsLoading(false);
            onClose();
        }
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-describedby='modal-price-history-description'
            aria-labelledby='modal-price-history-title'
        >
            <Stack sx={modalStyle}>
                <SectionHeader>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: 6,
                            p: 1,
                        }}
                    >
                        <Typography variant={'h5'}>Export PDF Quote</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon sx={{ color: 'white' }} />
                        </IconButton>
                    </Box>
                </SectionHeader>
                <Stack>
                    <Stack sx={{ p: 2, flexDirection: 'row', flexWrap: 'wrap', paddingTop: '10px' }}>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormCheckbox name={'isNationalAccount'} label='National Account?' control={control} />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormCheckbox name={'isPriceGroupOverride'} label='Price group quote?' control={control} />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormNumberField
                                name={'increaseValue'}
                                control={control}
                                label='Price Increase/Decrease'
                                type='number'
                            />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormDatepickerField
                                name={'effectiveDate'}
                                control={control}
                                label='Effective Date'
                                rules={{ required: 'Effective date is required' }}
                                fieldError={errors?.effectiveDate}
                            />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormTextField
                                name={'endUserContacts.contact'}
                                label={'End User Contact'}
                                control={control}
                            />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormTextField name={'endUserContacts.email'} label={'End User Email'} control={control} />
                        </Box>
                        <Box sx={{ padding: '10px', flex: '33%' }}>
                            <FormTextField name={'endUserContacts.phone'} label={'End User Phone'} control={control} />
                        </Box>
                    </Stack>
                    <Stack sx={{ p: 2, flexDirection: 'row', flexWrap: 'wrap', paddingTop: 0 }}>
                        <Box sx={{ padding: '10px', flex: '66%' }}>
                            <FormTextField name={'notes.additional'} label={'Additional Notes'} control={control} />
                        </Box>
                    </Stack>
                    {watch('isNationalAccount') && (
                        <>
                            <Stack sx={{ p: 2, flexDirection: 'row', flexWrap: 'wrap', paddingTop: 0 }}>
                                <Box sx={{ padding: '10px', flex: '33%' }}>
                                    <FormSelect
                                        control={control}
                                        label='National Account Presented By'
                                        name='nationalAccountContact'
                                        options={[
                                            { displayText: '<Blank>', value: 'blank' },
                                            ...(nationalAccountDropdownOptions || []),
                                        ]}
                                        isLoading={nationalAccountUsersIsLoading}
                                    />
                                </Box>
                                <Box sx={{ padding: '10px', flex: '33%' }}>
                                    <FormTextField
                                        name={'presentedBy.name'}
                                        label={'National Account Contact Name'}
                                        control={control}
                                        rules={{ required: 'Name is required' }}
                                        fieldError={errors?.presentedBy?.name}
                                    />
                                </Box>
                                <Box sx={{ padding: '10px', flex: '33%' }}>
                                    <FormTextField
                                        name={'presentedBy.email'}
                                        label={'National Account Email'}
                                        control={control}
                                        rules={{ required: 'Email is required' }}
                                        fieldError={errors?.presentedBy?.email}
                                    />
                                </Box>
                                <Box sx={{ padding: '10px', flex: '33%' }}>
                                    <FormTextField
                                        name={'presentedBy.phone'}
                                        label={'National Account Phone'}
                                        control={control}
                                        rules={{ required: 'Phone is required' }}
                                        fieldError={errors?.presentedBy?.phone}
                                    />
                                </Box>
                            </Stack>
                        </>
                    )}
                    <Stack sx={{ p: 2, flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'right' }}>
                        {isLoading ? (
                            <Loading />
                        ) : (
                            <>
                                <Button autoFocus onClick={onClose}>
                                    Exit
                                </Button>
                                <Button onClick={handlePdfQuoteExport} autoFocus>
                                    Confirm
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Modal>
    );
}
